import { useEffect, useRef, useState } from "react";
import { Check } from "lucide-react";

import { CurrentUserContent, Role, SettingGroup } from "@/types/setting";

import { DeleteSettingUser, SetRoles, UpdateUser } from "@/services/Setting";

import { DefaultRoleDesc } from "@/consts/Setting";

import { cn, isNotEmpty } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import CommonImg from "@/components/Common/Img";
import CommonInput from "@/components/Common/Input";

import ProfileImage from "@/assets/images/LNB/temp-profile.jpg";
import RedCloseIcon from "@/assets/images/Setting/red-close-icon.png";
import PencilIcon from "@/assets/images/Setting/pencil-icon.png";
import PlusIcon from "@/assets/images/plus-icon.png";
import BlackSearchIcon from "@/assets/images/black-search-icon.png";

import { ReactComponent as ArrowDownSvg } from "@/assets/images/arrow-down.svg";

type Props = {
  hasAdminRole: boolean;
  currentUser: CurrentUserContent;
  user: CurrentUserContent;
  userIndex: number;
  setUserList: React.Dispatch<React.SetStateAction<CurrentUserContent[]>>;
  groupList: SettingGroup[];
  roleList: Role[];
  onRefreshUserList: () => void;
  onRefreshCurrentUser: () => void;
};
export default function UserCard({
  hasAdminRole,
  currentUser,
  user,
  userIndex,
  setUserList,
  groupList,
  roleList,
  onRefreshUserList,
  onRefreshCurrentUser,
}: Props) {
  const isCurrentUser = currentUser.user_id === user.user_id;
  const hasEditRole = hasAdminRole || isCurrentUser;
  const canChangeRole = hasAdminRole;
  const [nameEditing, setNameEditing] = useState<boolean>(false);
  const [isAdding, setAdding] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>("");
  const [searchedGroupList, setSearchedGroupList] =
    useState<SettingGroup[]>(groupList);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [selectedRoleIds, setSelectedRoleIds] = useState<string[]>(
    user.roles?.map((r) => r.role_id) ?? []
  );
  const roleDropdownRef = useRef<HTMLDivElement>(null);
  const isUpdatingRoles = useRef(false);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        roleDropdownRef.current &&
        !roleDropdownRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // user.roles 변경 시 selectedRoleIds 동기화 (업데이트 중이 아닐 때만)
  useEffect(() => {
    if (!isUpdatingRoles.current) {
      setSelectedRoleIds(user.roles?.map((r) => r.role_id) ?? []);
    }
  }, [user.roles]);

  const handleClickDelete = async () => {
    try {
      const confirmFlag = window.confirm("정말 삭제하시겠습니까?");
      if (confirmFlag) {
        await DeleteSettingUser(user.user_id);
        onRefreshUserList();
      } else {
      }
    } catch (e) {
    } finally {
    }
  };

  const updateUser = async (params: string) => {
    try {
      const result = await UpdateUser(params);
      if (result.data.success) {
        showToast({
          message: "사용자 정보가 변경되었습니다.",
        });
        onRefreshUserList();
        setNameEditing(false);
        if (isCurrentUser) {
          onRefreshCurrentUser();
        }
      }
    } catch (e) {
    } finally {
    }
  };

  const setUserRoles = async (roleIds: string[]) => {
    try {
      isUpdatingRoles.current = true;
      // role_ids를 Python 리스트 형식으로 전송
      const roleIdsParam =
        roleIds.length > 0
          ? `[${roleIds.map((id) => `"${id}"`).join(", ")}]`
          : "[]";
      const params = `user_id=\"${user.user_id}\", role_ids=${roleIdsParam}`;
      const result = await SetRoles(params);
      if (result.data.success) {
        showToast({
          message: "사용자 권한이 변경되었습니다.",
        });
        // 잠시 후 리프레시 (로컬 상태와 충돌 방지)
        setTimeout(() => {
          isUpdatingRoles.current = false;
          onRefreshUserList();
          if (isCurrentUser) {
            onRefreshCurrentUser();
          }
        }, 300);
      } else {
        isUpdatingRoles.current = false;
      }
    } catch (e) {
      isUpdatingRoles.current = false;
    }
  };

  const handleToggleRole = (roleId: string) => {
    const newSelectedRoleIds = selectedRoleIds.includes(roleId)
      ? selectedRoleIds.filter((id) => id !== roleId)
      : [...selectedRoleIds, roleId];
    setSelectedRoleIds(newSelectedRoleIds);
    setUserRoles(newSelectedRoleIds);
  };

  const handleKeydownEnter = () => {
    const params = `user_id="${user.user_id}", last_name="${user.last_name}", first_name="${user.first_name}"`;
    updateUser(params);
  };

  const handleChangeSearchedGroupList = (value: string) => {
    setSearchedGroupList(() => {
      const newList = groupList.filter((group) => {
        return group.group_name.toLowerCase().includes(value.toLowerCase());
      });
      return isNotEmpty(value) ? newList : groupList;
    });
    setSearchText(value);
  };

  const handleSelectGroup = async (groupId: string) => {
    const params = `user_id="${user.user_id}", group_ids="${groupId}"`;
    updateUser(params);
  };

  return (
    <div className="border-[1px] border-gray-D rounded-[8px] p-[20px] flex flex-col justify-center gap-[10px]">
      {hasEditRole && (
        <div className="flex justify-end">
          <CommonImg
            src={RedCloseIcon}
            alt="Delete User"
            className="cursor-pointer"
            onClick={handleClickDelete}
          />
        </div>
      )}
      <div className="flex justify-between gap-[16px]">
        <div className="shrink-0">
          <CommonImg
            src={user.image_url ?? ProfileImage}
            alt="Profile"
            className="min-w-[80px] size-[80px] rounded-[50px]"
          />
        </div>
        <div className="flex flex-col gap-[8px] items-end flex-1 min-w-0">
          <div className="flex gap-[4px] items-center">
            {nameEditing ? (
              <div className="flex gap-[4px]">
                <CommonInput
                  className="w-[50px]"
                  defaultValue={user.last_name}
                  onChange={(value) =>
                    setUserList((prev) =>
                      prev.map((prevItem, prevIndex) =>
                        prevIndex === userIndex
                          ? {
                              ...prevItem,
                              last_name: value,
                            }
                          : { ...prevItem }
                      )
                    )
                  }
                  onKeyDownEnter={handleKeydownEnter}
                />
                <CommonInput
                  className="w-[50px]"
                  defaultValue={user.first_name}
                  onChange={(value) =>
                    setUserList((prev) =>
                      prev.map((prevItem, prevIndex) =>
                        prevIndex === userIndex
                          ? {
                              ...prevItem,
                              first_name: value,
                            }
                          : { ...prevItem }
                      )
                    )
                  }
                  onKeyDownEnter={handleKeydownEnter}
                />
              </div>
            ) : (
              <>
                <span className="text-[18px] font-bold">
                  {user.last_name}
                  {user.first_name}
                </span>
                {hasEditRole && (
                  <CommonImg
                    src={PencilIcon}
                    alt="Edit"
                    className="size-[8px] cursor-pointer"
                    onClick={() => {
                      setNameEditing(true);
                    }}
                  />
                )}
              </>
            )}
          </div>
          {canChangeRole ? (
            <div
              className="relative w-full max-w-[250px]"
              ref={roleDropdownRef}
            >
              {/* 선택된 권한 표시 영역 */}
              <div
                className="min-h-[30px] px-[8px] py-[4px] border border-gray-D rounded cursor-pointer flex items-center gap-[4px] flex-wrap"
                onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
              >
                {selectedRoleIds.length > 0 ? (
                  selectedRoleIds.map((roleId) => {
                    const role = roleList.find((r) => r.role_id === roleId);
                    return role ? (
                      <span
                        key={roleId}
                        className="px-[6px] py-[4px] rounded text-white text-[10px] font-bold text-center whitespace-nowrap"
                        style={{ backgroundColor: "rgba(167, 109, 110, 0.60)" }}
                      >
                        {role.description}
                      </span>
                    ) : null;
                  })
                ) : (
                  <span className="text-[10px] text-gray-B">
                    {DefaultRoleDesc}
                  </span>
                )}
                <ArrowDownSvg
                  className={cn(
                    "w-3 h-3 ml-auto transition-transform",
                    isRoleDropdownOpen && "rotate-180"
                  )}
                />
              </div>
              {/* 드롭다운 목록 */}
              {isRoleDropdownOpen && (
                <div className="absolute top-full left-0 w-full mt-1 bg-white border border-gray-D rounded shadow-lg z-20 max-h-[150px] overflow-y-auto">
                  {roleList.map((role) => {
                    const isSelected = selectedRoleIds.includes(role.role_id);
                    return (
                      <div
                        key={role.role_id}
                        className="px-[8px] py-[6px] flex items-center justify-between cursor-pointer hover:bg-gray-100 text-[10px]"
                        onClick={() => handleToggleRole(role.role_id)}
                      >
                        <span>{role.description}</span>
                        <div
                          className={cn(
                            "w-[12px] h-[12px] rounded-[2px] flex items-center justify-center",
                            isSelected ? "border-0" : "border border-gray-D"
                          )}
                          style={
                            isSelected
                              ? { backgroundColor: "rgba(167, 109, 110, 0.60)" }
                              : {}
                          }
                        >
                          {isSelected && (
                            <Check
                              className="w-[10px] h-[10px] text-white"
                              strokeWidth={3}
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-[4px] flex-wrap w-full">
              {user.roles && user.roles.length > 0 ? (
                user.roles.map((role) => (
                  <span
                    key={role.role_id}
                    className="px-[6px] py-[4px] rounded text-white text-[10px] font-bold text-center whitespace-nowrap"
                    style={{ backgroundColor: "rgba(167, 109, 110, 0.60)" }}
                  >
                    {role.description}
                  </span>
                ))
              ) : (
                <span className="text-[10px] text-gray-B">
                  {DefaultRoleDesc}
                </span>
              )}
            </div>
          )}
          {user.groups[0]?.group_name ? (
            <div className="px-[16px] py-[4px] rounded-[16px] bg-dark-red text-sm text-white font-bold">
              {user.groups[0]?.group_name}
            </div>
          ) : isAdding ? (
            <div className={cn("relative", { "z-10": !!searchedGroupList })}>
              <div
                className="w-[100px] px-[10px] py-[6px] border-dark-red border-[1px] rounded-[20px] flex justify-between items-center"
                onBlur={() => {
                  setSearchedGroupList([]);
                }}
              >
                <input
                  type="text"
                  className={cn(
                    "w-full h-[14px] focus:outline-none text-[12px] font-semibold"
                  )}
                  defaultValue={searchText}
                  onFocus={() => {
                    handleChangeSearchedGroupList(searchText);
                  }}
                  onChange={(e) => {
                    handleChangeSearchedGroupList(e.target.value);
                  }}
                />
                <CommonImg
                  src={BlackSearchIcon}
                  alt="Search"
                  className="size-[16px]"
                />
              </div>
              {searchedGroupList.length > 0 && (
                <div className="top-[10px] left-[0px] w-[100px] pt-[20px] z-[-1] absolute flex flex-col bg-white border-x-[1px] border-b-[1px] rounded-b-[4px] text-[12px] font-bold">
                  <div className="max-h-[100px] overflow-auto">
                    {searchedGroupList.map((group, groupIndex) => (
                      <div
                        key={groupIndex}
                        onClick={() => {
                          handleSelectGroup(group.group_id);
                        }}
                      >
                        <div className="p-[8px] flex gap-[8px] justify-center items-center hover:bg-gray-D cursor-pointer">
                          <div>{group.group_name}</div>
                        </div>
                        {groupIndex < searchedGroupList.length - 1 && <hr />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            hasEditRole && (
              <div
                className="w-[70px] px-[8px] py-[6px] flex justify-center items-center rounded-[20px] border-[1px] border-gray-D cursor-pointer"
                onClick={() => {
                  setAdding(true);
                }}
              >
                <CommonImg src={PlusIcon} alt="Plus" className="size-[12px]" />
              </div>
            )
          )}
          <div className="text-right text-gray-B font-semibold text-[10px]">
            {user.email}
          </div>
        </div>
      </div>
    </div>
  );
}
